import { useMemo } from "react"

import { loadStripe } from "@stripe/stripe-js"
import { Elements } from "@stripe/react-stripe-js"
import PropTypes from "prop-types"

// Per Stripe's guidance, `loadStripe` must run once at module scope rather than inside the
// component's render so a fresh promise is not allocated on every re-render. The developer-tools
// flag is keyed by email, so we cache one promise per (email, key) combination.
//
// https://docs.stripe.com/stripe-js/react#stripe-elements-provider
const stripePromisesByKey = new Map()

const getStripePromise = (email) => {
  const isIftttEmail = email && email.endsWith("@ifttt.com")
  const enableDeveloperTools =
    (window.App?.env === "development" || window.location.hostname === "web-rehearsal.ifttt.com") &&
    isIftttEmail
  const publishableKey = window.App?.stripePublishableKey || ""
  const cacheKey = `${publishableKey}|${enableDeveloperTools ? "dev" : "prod"}`

  let promise = stripePromisesByKey.get(cacheKey)
  if (!promise) {
    promise = loadStripe(publishableKey, {
      developerTools: {
        assistant: {
          enabled: enableDeveloperTools,
        },
      },
    })
    stripePromisesByKey.set(cacheKey, promise)
  }
  return promise
}

// Fixed tab label size — Stripe appearance has no media queries; resizing the viewport used to
// rebuild `options` and remount Elements (WEB-CLIENT-RJM / RVV).
const TAB_LABEL_SIZE = "16px"

export default function StripeJsContainer({ children, email, selectedPrice, selectedCurrency }) {
  const isIftttEmail = email && email.endsWith("@ifttt.com")
  const stripePromise = getStripePromise(email)
  const currency = selectedCurrency || "usd"

  let paymentMethodTypes = ["card"]
  if (window.App.paymentStrategy === "admin_test" && isIftttEmail) {
    paymentMethodTypes = ["card", "us_bank_account"]
  } else if (window.App.paymentStrategy === "stripe_dashboard") {
    paymentMethodTypes = []
  }

  // Same border width as `.Input` / `.Input:focus` so invalid state doesn’t shift layout (`_colors.scss` red).
  const errorColor = "#d0011b"
  const inputBorderInvalid = `4px solid ${errorColor}`
  // Match My Applets filter tabs (`my_applets.module.scss`: $tertiary-text-color / $primary-text-color).
  const tabTextPrimary = "#222222"
  const tabTextTertiary = "#999999"
  const tabUnderlineInactive = "4px solid transparent"
  // Match `.Input` horizontal inset (`8px 12px`) so tab labels line up with field text.
  const tabPadding = "4px 12px"
  const tabLineHeight = "1.2"
  const tabLabelPaddingTop = "6px"
  const tabRowSpacingBelow = "2px"

  const elementOptions = useMemo(() => ({
    mode: "subscription",
    amount: selectedPrice,
    currency,
    paymentMethodTypes: paymentMethodTypes,
    appearance: {
      // https://docs.stripe.com/elements/appearance-api?api-integration=paymentintents
      theme: "flat",
      labels: "floating",
      // `condensed` groups related card fields (expiry + CVC) so Stripe can keep them on one row where width allows.
      inputs: "condensed",
      variables: {
        // https://docs.stripe.com/elements/appearance-api?api-integration=paymentintents#variables
        fontFamily: '"Avenir Next", "Avenir Next W01", "Avenir", helvetica, arial, sans-serif',
        fontSizeBase: "16px",
        fontLineHeight: "1.1",
        fontWeightBold: "700",
        borderRadius: "8px",
        // Minimum horizontal slack so card row + expiry/CVC row fit in narrow checkout columns (~mid-width grid); Stripe stacks when below ~400px iframe width.
        gridColumnSpacing: "4px",
        gridRowSpacing: "10px",
        tabSpacing: "4px",
        colorPrimary: "#222222",
        colorPrimaryText: "#222222",
        colorText: "#222222",
        colorTextSecondary: "#77787D",
        // Match Not now / `$tertiary-text-color` (`#999`) — lighter placeholder than body text.
        colorTextPlaceholder: tabTextTertiary,
        colorDanger: errorColor,
        colorBackground: "#ffffff",
        colorIconTabSelected: tabTextPrimary,
        iconColor: tabTextPrimary,
        tabIconColor: tabTextTertiary,
        tabIconHoverColor: tabTextPrimary,
        tabIconSelectedColor: tabTextPrimary,
        iconCardErrorColor: errorColor,
        iconCardCvcErrorColor: errorColor,
        focusBoxShadow: "none",
        focusOutline: "none",
      },
      rules: {
        // https://docs.stripe.com/elements/appearance-api?api-integration=paymentintents#supported-css-properties
        ".Tab": {
          // Match `.Input` horizontal rhythm; My Applets–style tab treatment (tertiary / primary + underline).
          padding: tabPadding,
          marginBottom: tabRowSpacingBelow,
          fontSize: TAB_LABEL_SIZE,
          lineHeight: tabLineHeight,
          border: "none",
          borderBottom: tabUnderlineInactive,
          borderRadius: "0",
          backgroundColor: "transparent",
          color: tabTextTertiary,
          boxShadow: "none",
        },
        ".Tab:hover": {
          color: tabTextPrimary,
        },
        ".Tab--selected": {
          backgroundColor: "transparent",
          border: "none",
          borderBottom: tabUnderlineInactive,
          color: tabTextPrimary,
          boxShadow: "none",
        },
        ".Tab--selected:hover": {
          color: tabTextPrimary,
          borderBottom: tabUnderlineInactive,
        },
        ".Tab:focus": {
          color: tabTextPrimary,
          borderBottom: tabUnderlineInactive,
          boxShadow: "none",
          outline: "none",
        },
        ".Tab:focus-visible": {
          color: tabTextPrimary,
          borderBottom: tabUnderlineInactive,
          boxShadow: "none",
          outline: "none",
        },
        ".Tab--selected:focus": {
          borderBottom: tabUnderlineInactive,
          boxShadow: "none",
          outline: "none",
        },
        ".Tab--selected:focus-visible": {
          borderBottom: tabUnderlineInactive,
          boxShadow: "none",
          outline: "none",
        },
        ".TabLabel": {
          fontSize: TAB_LABEL_SIZE,
          fontWeight: "700",
          lineHeight: tabLineHeight,
          // Stripe defaults add horizontal inset; keep top alignment only and trim right padding.
          padding: `${tabLabelPaddingTop} 0 0 0`,
          paddingRight: "0",
          paddingLeft: "0",
        },
        ".Input": {
          fontSize: "1.125em",
          fontWeight: "700", // Same from labels resting
          border: "4px solid #2222221a",
          borderRadius: "8px",
          backgroundColor: "#ffffff",
          padding: "8px 12px",
        },
        ".Input:focus": {
          border: "4px solid #222222",
          boxShadow: "none",
        },
        ".Input--invalid": {
          border: inputBorderInvalid,
          boxShadow: "none",
        },
        ".Input--invalid:focus": {
          border: inputBorderInvalid,
          boxShadow: "none",
          outline: "none",
        },
        ".Input--invalid:hover": {
          border: inputBorderInvalid,
          boxShadow: "none",
        },
        ".Label--invalid": {
          color: errorColor,
        },
        ".Error": {
          color: errorColor,
          fontSize: "1.125em",
          fontWeight: "700",
          lineHeight: "1.5",
          // Near `_form_base.scss` `.field .note` (0.5em top, 0.2em left).
          margin: "0.5em 0 0 0.2em",
        },
        ".Label--resting": {
          fontSize: "1.125em",
          fontWeight: "700",
          color: "#22222299",
        },
        ".Label--floating": {
          // Larger + higher contrast than Stripe default so “Card number” etc. stay readable when floated.
          fontSize: "14px",
          fontWeight: "700",
          color: "#222222",
          lineHeight: "1.25",
        },
        // US bank institution list — align with `_form_base.scss` labels / list tone (Avenir + 700 + body scale).
        ".PickerItem": {
          padding: "10px 16px",
          fontSize: "1.125em",
          fontWeight: "700",
          color: "#222222",
          lineHeight: "1.45",
        },
        ".PickerItem--selected": {
          fontSize: "1.125em",
          fontWeight: "700",
          color: "#222222",
        },
        ".PickerItem--highlight": {
          fontSize: "1.125em",
          fontWeight: "700",
          color: "#222222",
        },
        ".PickerItem--disabled": {
          fontSize: "1.125em",
          fontWeight: "700",
          color: "#22222266",
        },
        ".PickerItem--new": {
          fontSize: "1.125em",
          fontWeight: "700",
          color: "#222222",
        },
        ".PickerItem:hover": {
          color: "#222222",
        },
        ".PickerItem:focus": {
          color: "#222222",
        },
        // “Enter bank details manually” / similar — `_links.scss` `.link-inline` + `.link-standalone`.
        ".Link": {
          fontSize: "1.125em",
          fontWeight: "700",
          lineHeight: "1.45",
          color: "#222222",
          textDecoration: "underline",
        },
        ".Link:hover": {
          color: "#222222",
          textDecoration: "underline",
        },
        ".Link:focus": {
          color: "#222222",
          textDecoration: "underline",
          outline: "none",
        },
        ".SecondaryLink": {
          fontSize: "1.125em",
          fontWeight: "700",
          lineHeight: "1.45",
          color: "#0099ff",
          textDecoration: "none",
        },
        ".SecondaryLink:hover": {
          color: "#0099ff",
          textDecoration: "none",
        },
        ".SecondaryLink:focus": {
          color: "#0099ff",
          textDecoration: "none",
          outline: "none",
        },
        // Supporting copy under bank search (matches `colorTextSecondary` / `$secondary-text-color` tone).
        ".Text": {
          fontSize: "1.125em",
          fontWeight: "600",
          lineHeight: "1.5",
          color: "#666666",
        },
        ".TermsText": {
          fontSize: "1.125em",
          fontWeight: "600",
          lineHeight: "1.5",
          color: "#666666",
        },
        ".TermsLink": {
          fontSize: "1.125em",
          fontWeight: "700",
          lineHeight: "1.45",
          color: "#0099ff",
          textDecoration: "none",
        },
        ".TermsLink:hover": {
          color: "#0099ff",
          textDecoration: "none",
        },
        ".TermsLink:focus": {
          color: "#0099ff",
          outline: "none",
        },
        ".PickerAction": {
          fontSize: "1.125em",
          fontWeight: "700",
          lineHeight: "1.45",
          color: "#222222",
          textDecoration: "underline",
        },
        ".PickerAction:hover": {
          color: "#222222",
        },
        ".PickerAction:focus": {
          color: "#222222",
          outline: "none",
        },
      },
    },
  }), [selectedPrice, currency, paymentMethodTypes.join(",")])

  const elementsKey = `${selectedPrice}-${currency}`

  // Usage of 'stripe' and 'elements' must be in a child component of <Elements>
  return (
    <Elements key={elementsKey} stripe={stripePromise} options={elementOptions}>
      {children}
    </Elements>
  )
}

StripeJsContainer.propTypes = {
  children: PropTypes.node.isRequired,
  email: PropTypes.string,
  selectedPrice: PropTypes.number,
  selectedCurrency: PropTypes.string,
}
